import { sortedIndexBy, filter, sortBy, uniqBy } from 'lodash';
import { milliseconds } from 'date-fns';
import { migrate } from '@/versioning';
import { required } from './common';
import { dateToYmd, ymdComponents } from '@/util';

export class History {
    constructor(date, units, notes, version = null) {
        this.date = date ?? required('date');
        this.units = Number(units ?? 0);
        this.version = version ?? historyMigrations.length;
        this.notes = notes;
    }
}

const symDateHash = Symbol('hash');
export class HistoryCollection extends Array {
    constructor(...elements) {
        super(...elements);
        this[symDateHash] = {};
        this.rebuild();
    }

    update(entry) {
        const hash = this[symDateHash];
        let index = hash[entry.date];
        if (index != null) {
            this[index] = entry;
        } else {
            index = sortedIndexBy(this, entry, x => {
                const [y, m, d] = ymdComponents(x.date);
                return milliseconds({ years: y, months: m, days: d});
            });

            this.splice(index, 0, entry);
        }
    }
	
    removeByDate(ymd) {
        const index = this[symDateHash][ymd];
        if (index != null)
            this.splice(index, 1);
    }

    getByDate(date) {
        const ymd = dateToYmd(date);
        const index = this[symDateHash][ymd];
        if (index != null)
            return this[index];
        return null;
    }

    push(...items) {
        const result = super.push(...items);
        this.rebuild();
        return result;
    }

    splice(start, deleteCount, ...items) {
        const result = super.splice(start, deleteCount, ...items);
        this.rebuild();
        return result;
    }

    copyWithin(target, start, end) {
        const result = super.copyWithin(target, start, end);
        this.rebuild();
        return result;
    }

    fill(value, start) {
        const result = super.fill(value, start);
        this.rebuild();
        return result;
    }

    pop() {
        const result = super.pop();
        this.rebuild();
        return result;
    }

    reverse() {
        const result = super.reverse();
        this.rebuild();
        return result;
    }

    shift() {
        const result = super.shift();
        this.rebuild();
        return result;
    }

    unshift(...items) {
        const result = super.unshift(...items);
        this.rebuild();
        return result;
    }

    sort(compareFn) {
        const result = super.sort(compareFn);
        this.rebuild();
        return result;
    }

    rebuild() {
        const hash = this[symDateHash] = { };
        for (let i = 0; i < this.length; ++i){
            const item = this[i];
            if (item != null)
                hash[this[i].date] = i;
        }
    }
}

export function parseHistory(jsonArray) {
    let result = [];
    for (const j of jsonArray) {
        migrate('history', j, historyMigrations);
        result.push(new History(
            j.date,
            j.units,
            j.notes,
            j.version
        ));
    }

    result = filter(result, x => x.units > 0 || x.notes);
    result = sortBy(result, 'date');
    result = uniqBy(result, 'date');
    return new HistoryCollection(...result);
}

export const historyMigrations = [
    function initial(_history, _version) {
        //  no actual action required
    },
    function historyDateAsPlainYMD(history, _version) {
        history.date = dateToYmd(new Date(history.date));
    },
    function ensureNewValidatorCompliance(history, _version) {
        history.units = Math.min(history.units ?? 0, 1000000);
    },
    function addNotesField(history, _version) {
        history.notes = '';
    },
    function removeKeyField(history, _version) {
        delete history.key;
    },
];